import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Plus, Trash2, Share2, LogOut, Menu, X, Mail, ChevronDown,
  Heart, AlertTriangle, Stethoscope, Phone, Activity, Syringe,
  FlaskConical, Shield, CheckCircle, RefreshCw, Edit3, Save,
  User, Droplet, Calendar, Upload, FileText, Printer
} from 'lucide-react';

const SUPABASE_URL  = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY  = import.meta.env.VITE_SUPABASE_ANON_KEY;
const TURNSTILE_KEY = '0x4AAAAAAE98vt31IzGsz8b1';

// ─── Supabase ─────────────────────────────────────────────────────────────────
class SB {
  constructor() { this.token = localStorage.getItem('sb_token'); }
  hdr(extra={}) {
    return { 'Content-Type':'application/json', apikey:SUPABASE_KEY,
      ...(this.token && { Authorization:`Bearer ${this.token}` }), ...extra };
  }
  async signup(email, password) {
    const r = await fetch(`${SUPABASE_URL}/auth/v1/signup`,
      { method:'POST', headers:this.hdr(), body:JSON.stringify({email,password}) });
    const d = await r.json();
    if (!r.ok) throw new Error(d.error_description || d.msg || 'Signup failed');
    if (!d.session) throw new Error('Check your email to confirm your account, then sign in.');
    this.token = d.session.access_token;
    localStorage.setItem('sb_token', this.token);
    localStorage.setItem('sb_uid', d.user.id);
    return d;
  }
  async login(email, password) {
    const r = await fetch(`${SUPABASE_URL}/auth/v1/token?grant_type=password`,
      { method:'POST', headers:this.hdr(), body:JSON.stringify({email,password}) });
    let d;
    try { d = await r.json(); } catch(e) { throw new Error('Server response error: ' + r.status + ' ' + r.statusText); }
    if (!r.ok) {
      const msg = d.error_description || d.msg || d.message || d.error || JSON.stringify(d);
      throw new Error('Login failed (' + r.status + '): ' + msg);
    }
    if (!d.access_token) throw new Error('No token received. Response: ' + JSON.stringify(d));
    this.token = d.access_token;
    localStorage.setItem('sb_token', this.token);
    localStorage.setItem('sb_uid', d.user.id);
    return d;
  }
  async logout() {
    await fetch(`${SUPABASE_URL}/auth/v1/logout`,
      { method:'POST', headers:this.hdr() }).catch(()=>{});
    localStorage.removeItem('sb_token');
    localStorage.removeItem('sb_uid');
    this.token = null;
  }
  async getProfile(uid) {
    const r = await fetch(`${SUPABASE_URL}/rest/v1/medical_profiles?user_id=eq.${uid}&select=*`,
      { headers:this.hdr() });
    if (!r.ok) return null;
    const d = await r.json();
    return d[0] || null;
  }
  async saveProfile(uid, data) {
    const payload = {
      user_id:uid,
      medications:         data.medications        || [],
      health_issues:       data.healthIssues        || [],
      doctors:             data.doctors             || [],
      allergies:           data.allergies           || [],
      emergency_contacts:  data.emergencyContacts   || [],
      vital_signs:         data.vitalSigns          || [],
      vaccination_records: data.vaccinationRecords  || [],
      lab_results:         data.labResults          || [],
      insurance_info:      data.insuranceInfo       || [],
      patient_profile:     data.patientProfile      || {},
      updated_at:          new Date().toISOString(),
    };
    // Try PATCH first, then POST if no row exists
    const patch = await fetch(
      `${SUPABASE_URL}/rest/v1/medical_profiles?user_id=eq.${uid}`,
      { method:'PATCH', headers:this.hdr({'Prefer':'return=representation'}), body:JSON.stringify(payload) });
    const pd = await patch.json();
    if (!Array.isArray(pd) || pd.length === 0) {
      await fetch(`${SUPABASE_URL}/rest/v1/medical_profiles`,
        { method:'POST', headers:this.hdr({'Prefer':'return=minimal'}), body:JSON.stringify(payload) });
    }
  }
}

// ─── IndexedDB ────────────────────────────────────────────────────────────────
async function initDB() {
  return new Promise((res,rej) => {
    const r = indexedDB.open('MedicalPWA',1);
    r.onupgradeneeded = e => {
      const db = e.target.result;
      if (!db.objectStoreNames.contains('data')) db.createObjectStore('data',{keyPath:'id'});
    };
    r.onsuccess = () => res(r.result);
    r.onerror   = () => rej(r.error);
  });
}
async function saveLocal(data) {
  const db = await initDB();
  return new Promise((res,rej) => {
    const tx = db.transaction('data','readwrite');
    tx.objectStore('data').put({id:'medical', data, ts:Date.now()});
    tx.oncomplete = res; tx.onerror = () => rej(tx.error);
  });
}
async function getLocal() {
  const db = await initDB();
  return new Promise((res,rej) => {
    const tx = db.transaction('data','readonly');
    const r  = tx.objectStore('data').get('medical');
    r.onsuccess = () => res(r.result?.data || null);
    r.onerror   = () => rej(r.error);
  });
}

// ─── Config ───────────────────────────────────────────────────────────────────
const EMPTY = {
  patientProfile:{ firstName:'', lastName:'', dob:'', bloodType:'', gender:'', isdCode:'+91', phone:'', address:'' },
  medications:[], healthIssues:[], doctors:[], allergies:[],
  emergencyContacts:[], vitalSigns:[], vaccinationRecords:[], labResults:[], insuranceInfo:[],
};

const SECTIONS = [
  { key:'medications',        label:'Medications',       icon:Heart,         color:'#f43f5e', bg:'rgba(244,63,94,0.12)',  fields:['name','dosage','frequency','reason','prescribedBy','startDate'] },
  { key:'allergies',          label:'Allergies',          icon:AlertTriangle, color:'#f97316', bg:'rgba(249,115,22,0.12)', fields:['allergen','reaction','severity'] },
  { key:'healthIssues',       label:'Health Conditions',  icon:Activity,      color:'#a78bfa', bg:'rgba(167,139,250,0.12)',fields:['condition','since','status','notes'] },
  { key:'doctors',            label:'My Doctors',         icon:Stethoscope,   color:'#38bdf8', bg:'rgba(56,189,248,0.12)', fields:['name','specialty','clinic','phone','email','address'] },
  { key:'emergencyContacts',  label:'Emergency Contacts', icon:Phone,         color:'#fb7185', bg:'rgba(251,113,133,0.12)',fields:['name','relationship','phone','email'] },
  { key:'vitalSigns',         label:'Vital Signs',        icon:Activity,      color:'#34d399', bg:'rgba(52,211,153,0.12)', fields:['type','value','unit','date'] },
  { key:'vaccinationRecords', label:'Vaccinations',       icon:Syringe,       color:'#22d3ee', bg:'rgba(34,211,238,0.12)', fields:['vaccine','date','dosage','nextDue','provider'] },
  { key:'labResults',         label:'Lab Results',        icon:FlaskConical,  color:'#818cf8', bg:'rgba(129,140,248,0.12)',fields:['test','date','result','unit','referenceRange','notes'] },
  { key:'insuranceInfo',      label:'Insurance',          icon:Shield,        color:'#fbbf24', bg:'rgba(251,191,36,0.12)', fields:['provider','policyNumber','groupNumber','coverage','expiryDate'] },
];

const sb = new SB();

// ─── Helpers ──────────────────────────────────────────────────────────────────
const ISD_CODES = [
  { code:'+91',  flag:'🇮🇳', name:'India' },
  { code:'+1',   flag:'🇺🇸', name:'USA/Canada' },
  { code:'+44',  flag:'🇬🇧', name:'UK' },
  { code:'+61',  flag:'🇦🇺', name:'Australia' },
  { code:'+971', flag:'🇦🇪', name:'UAE' },
  { code:'+966', flag:'🇸🇦', name:'Saudi Arabia' },
  { code:'+974', flag:'🇶🇦', name:'Qatar' },
  { code:'+965', flag:'🇰🇼', name:'Kuwait' },
  { code:'+968', flag:'🇴🇲', name:'Oman' },
  { code:'+973', flag:'🇧🇭', name:'Bahrain' },
  { code:'+92',  flag:'🇵🇰', name:'Pakistan' },
  { code:'+880', flag:'🇧🇩', name:'Bangladesh' },
  { code:'+94',  flag:'🇱🇰', name:'Sri Lanka' },
  { code:'+977', flag:'🇳🇵', name:'Nepal' },
  { code:'+60',  flag:'🇲🇾', name:'Malaysia' },
  { code:'+65',  flag:'🇸🇬', name:'Singapore' },
  { code:'+49',  flag:'🇩🇪', name:'Germany' },
  { code:'+33',  flag:'🇫🇷', name:'France' },
  { code:'+39',  flag:'🇮🇹', name:'Italy' },
  { code:'+81',  flag:'🇯🇵', name:'Japan' },
  { code:'+86',  flag:'🇨🇳', name:'China' },
  { code:'+82',  flag:'🇰🇷', name:'South Korea' },
  { code:'+55',  flag:'🇧🇷', name:'Brazil' },
  { code:'+27',  flag:'🇿🇦', name:'South Africa' },
  { code:'+20',  flag:'🇪🇬', name:'Egypt' },
];

const BLOOD_TYPES = ['A+','A-','B+','B-','AB+','AB-','O+','O-'];
const GENDERS     = ['Male','Female','Other','Prefer not to say'];

function fieldLabel(key) {
  return key.replace(/([A-Z])/g,' $1').replace(/^./,s=>s.toUpperCase());
}

function generateReport(data) {
  const p = data.patientProfile || {};
  const now = new Date().toLocaleDateString('en-US',{year:'numeric',month:'long',day:'numeric'});
  let out = '';
  out += `╔══════════════════════════════════════════╗\n`;
  out += `║         MEDICAL PROFILE REPORT           ║\n`;
  out += `║         Generated: ${now.padEnd(21)}║\n`;
  out += `╚══════════════════════════════════════════╝\n\n`;
  if (p.fullName) {
    out += `PATIENT INFORMATION\n${'─'.repeat(42)}\n`;
    if (p.fullName)   out += `Name:       ${p.fullName}\n`;
    if (p.dob)        out += `DOB:        ${p.dob}\n`;
    if (p.gender)     out += `Gender:     ${p.gender}\n`;
    if (p.bloodType)  out += `Blood Type: ${p.bloodType}\n`;
    if (p.phone)      out += `Phone:      ${p.isdCode || ''} ${p.phone}\n`;
    if (p.address)    out += `Address:    ${p.address}\n`;
    out += '\n';
  }
  if (data.allergies?.length) {
    out += `⚠️  ALLERGIES (IMPORTANT)\n${'─'.repeat(42)}\n`;
    data.allergies.forEach(a => out += `  • ${a.allergen}: ${a.reaction} [${a.severity}]\n`);
    out += '\n';
  }
  if (data.medications?.length) {
    out += `💊 CURRENT MEDICATIONS\n${'─'.repeat(42)}\n`;
    data.medications.forEach(m => out += `  • ${m.name} ${m.dosage} — ${m.frequency}${m.prescribedBy ? ` (Dr. ${m.prescribedBy})` : ''}\n`);
    out += '\n';
  }
  if (data.healthIssues?.length) {
    out += `🏥 HEALTH CONDITIONS\n${'─'.repeat(42)}\n`;
    data.healthIssues.forEach(h => out += `  • ${h.condition} (Since: ${h.since}) [${h.status}]\n`);
    out += '\n';
  }
  if (data.doctors?.length) {
    out += `👨‍⚕️ HEALTHCARE PROVIDERS\n${'─'.repeat(42)}\n`;
    data.doctors.forEach(d => out += `  • ${d.name} — ${d.specialty}\n    ${d.clinic} | ${d.phone}\n`);
    out += '\n';
  }
  if (data.emergencyContacts?.length) {
    out += `🚨 EMERGENCY CONTACTS\n${'─'.repeat(42)}\n`;
    data.emergencyContacts.forEach(e => out += `  • ${e.name} (${e.relationship}): ${e.phone}\n`);
    out += '\n';
  }
  if (data.vaccinationRecords?.length) {
    out += `💉 VACCINATIONS\n${'─'.repeat(42)}\n`;
    data.vaccinationRecords.forEach(v => out += `  • ${v.vaccine} — ${v.date} (Next: ${v.nextDue})\n`);
    out += '\n';
  }
  if (data.insuranceInfo?.length) {
    out += `🛡️  INSURANCE\n${'─'.repeat(42)}\n`;
    data.insuranceInfo.forEach(i => out += `  • ${i.provider} | Policy: ${i.policyNumber}\n`);
    out += '\n';
  }
  out += `${'─'.repeat(42)}\nGenerated by MediCard — medicard-pwa.pages.dev\n`;
  return out;
}

// ─── Main App ─────────────────────────────────────────────────────────────────
export default function App() {
  const [user,  setUser]  = useState(null);
  const [data,  setData]  = useState(null);
  const [sync,  setSync]  = useState('idle');
  const [menu,  setMenu]  = useState(false);
  const [toast, setToast] = useState('');
  const [tab,   setTab]   = useState('profile'); // 'profile' | 'records'
  const [importOpen, setImportOpen] = useState(false);

  const showToast = (msg, dur=3000) => { setToast(msg); setTimeout(()=>setToast(''),dur); };

  // Cloud → local merge helper
  const fromCloud = (profile) => ({
    patientProfile:     profile.patient_profile      || {},
    medications:        profile.medications           || [],
    healthIssues:       profile.health_issues         || [],
    doctors:            profile.doctors               || [],
    allergies:          profile.allergies             || [],
    emergencyContacts:  profile.emergency_contacts    || [],
    vitalSigns:         profile.vital_signs           || [],
    vaccinationRecords: profile.vaccination_records   || [],
    labResults:         profile.lab_results           || [],
    insuranceInfo:      profile.insurance_info        || [],
  });

  useEffect(() => {
    (async () => {
      const token = localStorage.getItem('sb_token');
      const uid   = localStorage.getItem('sb_uid');
      const local = await getLocal();
      if (token && uid) {
        sb.token = token;
        setUser({ token, uid });
        try {
          const profile = await sb.getProfile(uid);
          if (profile) { const cloud = fromCloud(profile); setData(cloud); await saveLocal(cloud); }
          else setData(local || EMPTY);
        } catch { setData(local || EMPTY); }
      } else {
        setData(local || EMPTY);
      }
    })();
    if ('serviceWorker' in navigator) {
      // Register SW - SW v4 self-clears old caches on activate
      navigator.serviceWorker.register('/sw.js', { updateViaCache: 'none' })
        .then(reg => {
          // Force check for updates immediately
          reg.update();
        }).catch(() => {});

      // Listen for SW controlling page - reload to get fresh content
      let refreshed = false;
      navigator.serviceWorker.addEventListener('controllerchange', () => {
        if (!refreshed) {
          refreshed = true;
          window.location.reload();
        }
      });
    }
  }, []);

  const update = useCallback(async (section, value) => {
    const updated = { ...data, [section]: value };
    setData(updated);
    await saveLocal(updated);
    const uid = localStorage.getItem('sb_uid');
    if (uid && sb.token) {
      setSync('syncing');
      try { await sb.saveProfile(uid, updated); setSync('synced'); setTimeout(()=>setSync('idle'),2500); }
      catch { setSync('error'); setTimeout(()=>setSync('idle'),3000); }
    }
  }, [data]);

  const handleLogin = async (email, password) => {
    await sb.login(email, password);
    const uid = localStorage.getItem('sb_uid');
    // Force page reload for Edge compatibility
    // This ensures React re-renders correctly after login
    setUser({ token:sb.token, uid });
    try {
      const profile = await sb.getProfile(uid);
      if (profile) { 
        const cloud = fromCloud(profile); 
        setData(cloud); 
        await saveLocal(cloud); 
      } else {
        setData(EMPTY);
      }
    } catch(e) { 
      setData(EMPTY); 
    }
    showToast('Welcome back! 👋');
    // Small delay then force re-render for Edge
    setTimeout(() => {
      setUser(u => u ? {...u, loaded: true} : u);
    }, 100);
  };

  const handleSignup = async (email, password) => {
    await sb.signup(email, password);
    setUser({ token:sb.token, uid:localStorage.getItem('sb_uid') });
    setData(EMPTY);
    showToast('Account created! 🎉');
  };

  const handleLogout = async () => {
    await sb.logout(); setUser(null); setData(EMPTY); setMenu(false); showToast('Signed out.');
  };

  const exportEmail = () => {
    const body = generateReport(data);
    window.location.href = `mailto:?subject=${encodeURIComponent('My Medical Profile — MediCard')}&body=${encodeURIComponent(body)}`;
    setMenu(false);
  };

  const exportWhatsApp = () => {
    const p = data.patientProfile || {};
    let m = `📋 *My Medical Profile*\n`;
    if (p.firstName || p.lastName) m += `👤 ${p.firstName || ''} ${p.lastName || ''}`;
    if (p.bloodType) m += ` | 🩸 ${p.bloodType}`;
    m += `\n📅 ${new Date().toLocaleDateString()}\n\n`;
    if (data.allergies?.length)   { m+=`⚠️ *Allergies*\n`;  data.allergies.forEach(a=>m+=`• ${a.allergen}: ${a.reaction}\n`); m+='\n'; }
    if (data.medications?.length) { m+=`💊 *Medications*\n`; data.medications.forEach(x=>m+=`• ${x.name} ${x.dosage} — ${x.frequency}\n`); m+='\n'; }
    if (data.healthIssues?.length){ m+=`🏥 *Conditions*\n`;  data.healthIssues.forEach(x=>m+=`• ${x.condition}\n`); m+='\n'; }
    if (data.doctors?.length)     { m+=`👨‍⚕️ *Doctors*\n`;  data.doctors.forEach(x=>m+=`• ${x.name}: ${x.phone}\n`); }
    window.open(`https://wa.me/?text=${encodeURIComponent(m)}`,'_blank');
    setMenu(false);
  };


  // Import records from pasted JSON. Adds to existing data, never deletes.
  const importAll = async (obj) => {
    const keys = SECTIONS.map(x=>x.key);
    const updated = { ...data };
    let added = 0;
    keys.forEach(k => {
      if (Array.isArray(obj[k]) && obj[k].length) {
        const existing = updated[k] || [];
        const fresh = obj[k].filter(n => !existing.some(e => JSON.stringify(e) === JSON.stringify(n)));
        updated[k] = [...existing, ...fresh];
        added += fresh.length;
      }
    });
    if (obj.patientProfile && typeof obj.patientProfile === 'object') {
      updated.patientProfile = { ...(data.patientProfile||{}), ...obj.patientProfile };
    }
    setData(updated);
    await saveLocal(updated);
    const uid = localStorage.getItem('sb_uid');
    if (uid && sb.token) {
      setSync('syncing');
      try { await sb.saveProfile(uid, updated); setSync('synced'); setTimeout(()=>setSync('idle'),2500); }
      catch { setSync('error'); setTimeout(()=>setSync('idle'),3000); }
    }
    return added;
  };

  const printReport = () => {
    const report = generateReport(data);
    const w = window.open('','_blank');
    w.document.write(`<html><head><title>MediCard Report</title>
      <style>body{font-family:monospace;padding:40px;white-space:pre-wrap;font-size:13px;line-height:1.6;color:#1a1a1a;max-width:700px;margin:0 auto;}
      @media print{body{padding:20px;}}</style></head>
      <body>${report}</body></html>`);
    w.document.close();
    w.print();
    setMenu(false);
  };

  const totalRecords = SECTIONS.reduce((s,sec)=>s+(data?.[sec.key]?.length||0),0);

  if (!user || !data) return <LoginScreen onLogin={handleLogin} onSignup={handleSignup} />;

  return (
    <div style={{minHeight:'100vh',background:'linear-gradient(135deg,#0f0c29 0%,#302b63 50%,#24243e 100%)',fontFamily:"'Inter',system-ui,sans-serif",overflowX:'hidden'}}>

      {/* Toast */}
      {toast && (
        <div style={{position:'fixed',top:16,left:'50%',transform:'translateX(-50%)',background:'rgba(255,255,255,0.13)',backdropFilter:'blur(20px)',color:'#fff',padding:'10px 22px',borderRadius:50,zIndex:9999,fontSize:13,fontWeight:600,border:'1px solid rgba(255,255,255,0.2)',boxShadow:'0 8px 32px rgba(0,0,0,0.4)',whiteSpace:'nowrap'}}>
          {toast}
        </div>
      )}

      {/* Header */}
      <header style={{position:'sticky',top:0,zIndex:100,background:'rgba(15,12,41,0.88)',backdropFilter:'blur(24px)',borderBottom:'1px solid rgba(255,255,255,0.07)'}}>
        <div style={{maxWidth:900,margin:'0 auto',padding:'0 20px',height:62,display:'flex',alignItems:'center',justifyContent:'space-between'}}>
          <div style={{display:'flex',alignItems:'center',gap:12}}>
            <div style={{width:36,height:36,borderRadius:11,background:'linear-gradient(135deg,#f43f5e,#8b5cf6)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:17}}>🏥</div>
            <div>
              <div style={{color:'#fff',fontWeight:700,fontSize:16,letterSpacing:'-0.3px'}}>MediCard</div>
              <div style={{color:'rgba(255,255,255,0.35)',fontSize:10.5}}>{totalRecords} records · {data.patientProfile?.firstName ? `${data.patientProfile.firstName} ${data.patientProfile.lastName || ''}`.trim() : 'Set up your profile'}</div>
            </div>
          </div>
          <div style={{display:'flex',alignItems:'center',gap:10}}>
            {sync==='syncing' && <span style={{fontSize:11,color:'#a78bfa',display:'flex',alignItems:'center',gap:4}}><RefreshCw size={11} style={{animation:'spin 1s linear infinite'}}/>Syncing</span>}
            {sync==='synced'  && <span style={{fontSize:11,color:'#34d399',display:'flex',alignItems:'center',gap:4}}><CheckCircle size={11}/>Saved to cloud</span>}
            {sync==='error'   && <span style={{fontSize:11,color:'#fb7185'}}>⚠ Sync failed</span>}
            <button onClick={()=>setMenu(!menu)} style={{width:36,height:36,borderRadius:10,background:'rgba(255,255,255,0.07)',border:'1px solid rgba(255,255,255,0.1)',color:'#fff',display:'flex',alignItems:'center',justifyContent:'center',cursor:'pointer'}}>
              {menu ? <X size={17}/> : <Menu size={17}/>}
            </button>
          </div>
        </div>

        {menu && (
          <div style={{position:'absolute',right:16,top:66,background:'rgba(18,16,46,0.98)',backdropFilter:'blur(24px)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:16,padding:8,minWidth:220,boxShadow:'0 24px 64px rgba(0,0,0,0.6)',zIndex:200}}>
            {[
              {icon:<Mail size={15}/>,    label:'Email my profile',   fn:exportEmail},
              {icon:<Share2 size={15}/>,  label:'Share on WhatsApp',  fn:exportWhatsApp},
              {icon:<Upload size={15}/>,  label:'Import records',     fn:()=>{setImportOpen(true);setMenu(false);}},
              {icon:<Printer size={15}/>, label:'Print / Save PDF',   fn:printReport},
              {icon:<LogOut size={15}/>,  label:'Sign out',           fn:handleLogout, danger:true},
            ].map((item,i)=>(
              <button key={i} onClick={item.fn}
                style={{width:'100%',display:'flex',alignItems:'center',gap:10,padding:'10px 14px',borderRadius:10,background:'transparent',border:'none',color:item.danger?'#fb7185':'rgba(255,255,255,0.75)',fontSize:13.5,cursor:'pointer',textAlign:'left'}}
                onMouseEnter={e=>e.currentTarget.style.background='rgba(255,255,255,0.07)'}
                onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
                {item.icon} {item.label}
              </button>
            ))}
          </div>
        )}
      </header>

      {/* Tab Bar */}
      <div style={{maxWidth:900,margin:'0 auto',padding:'24px 20px 0'}}>
        <div style={{display:'flex',gap:8,marginBottom:28}}>
          {[{id:'profile',label:'👤 My Profile'},{id:'records',label:'📋 Health Records'}].map(t=>(
            <button key={t.id} onClick={()=>setTab(t.id)}
              style={{padding:'9px 20px',borderRadius:50,border:'none',fontSize:13.5,fontWeight:600,cursor:'pointer',transition:'all 0.2s',
                background: tab===t.id ? 'linear-gradient(135deg,#8b5cf6,#f43f5e)' : 'rgba(255,255,255,0.07)',
                color: tab===t.id ? '#fff' : 'rgba(255,255,255,0.45)',
                boxShadow: tab===t.id ? '0 4px 20px rgba(139,92,246,0.4)' : 'none'}}>
              {t.label}
            </button>
          ))}
        </div>

        {/* Profile Tab */}
        {tab==='profile' && (
          <PatientProfile profile={data.patientProfile||{}} onSave={val=>update('patientProfile',val)} />
        )}

        {/* Records Tab */}
        {tab==='records' && (
          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(270px,1fr))',gap:14,paddingBottom:60}}>
            {SECTIONS.map(sec=>(
              <SectionCard key={sec.key} section={sec} items={data[sec.key]||[]} onUpdate={items=>update(sec.key,items)} />
            ))}
          </div>
        )}
      </div>

      {importOpen && <ImportModal onClose={()=>setImportOpen(false)} onImport={async(o)=>{const n=await importAll(o);showToast(`Imported ${n} record${n===1?'':'s'} ✅`);setImportOpen(false);}} />}
      <style>{`@keyframes spin{from{transform:rotate(0deg)}to{transform:rotate(360deg)}}`}</style>
    </div>
  );
}


// ─── Import Modal ─────────────────────────────────────────────────────────────
function ImportModal({ onClose, onImport }) {
  const [text, setText] = useState('');
  const [err,  setErr]  = useState('');
  const [busy, setBusy] = useState(false);

  const run = async () => {
    setErr('');
    let obj;
    try { obj = JSON.parse(text); }
    catch { setErr('This does not look right. Paste the whole block exactly as it was given to you.'); return; }
    if (!obj || typeof obj !== 'object' || Array.isArray(obj)) { setErr('Unexpected format. Paste the full block.'); return; }
    setBusy(true);
    try { await onImport(obj); } catch(e) { setErr('Import failed: ' + e.message); setBusy(false); }
  };

  return (
    <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',backdropFilter:'blur(6px)',zIndex:9000,display:'flex',alignItems:'center',justifyContent:'center',padding:20}}>
      <div style={{width:'100%',maxWidth:560,background:'#1a1740',border:'1px solid rgba(255,255,255,0.12)',borderRadius:20,padding:24}}>
        <h2 style={{color:'#fff',fontSize:18,fontWeight:700,margin:'0 0 6px'}}>Import records</h2>
        <p style={{color:'rgba(255,255,255,0.5)',fontSize:13,margin:'0 0 14px',lineHeight:1.5}}>
          Paste the block you were given. It is <b>added</b> to what you already have. Nothing is deleted, and exact duplicates are skipped.
        </p>
        <textarea value={text} onChange={e=>setText(e.target.value)} placeholder="Paste here…"
          style={{width:'100%',height:220,background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.12)',borderRadius:12,padding:12,color:'#fff',fontSize:12,fontFamily:'monospace',outline:'none',boxSizing:'border-box',resize:'vertical'}}/>
        {err && <div style={{color:'#fca5a5',fontSize:13,marginTop:10}}>{err}</div>}
        <div style={{display:'flex',gap:10,marginTop:14}}>
          <button onClick={run} disabled={busy||!text.trim()} style={{flex:1,padding:12,borderRadius:12,border:'none',color:'#fff',fontWeight:700,fontSize:14,cursor:busy||!text.trim()?'not-allowed':'pointer',background:busy||!text.trim()?'rgba(139,92,246,0.3)':'linear-gradient(135deg,#8b5cf6,#f43f5e)'}}>
            {busy?'Importing…':'Import'}
          </button>
          <button onClick={onClose} style={{padding:'12px 20px',borderRadius:12,background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',color:'rgba(255,255,255,0.6)',fontSize:14,cursor:'pointer'}}>Cancel</button>
        </div>
      </div>
    </div>
  );
}

// ─── Patient Profile ──────────────────────────────────────────────────────────
function PatientProfile({ profile, onSave }) {
  const [editing, setEditing] = useState(false);
  const [form, setForm] = useState(profile);

  useEffect(() => { setForm(profile); }, [profile]);

  const save = () => { onSave(form); setEditing(false); };

  const card = (label, value, icon) => value ? (
    <div key={label} style={{background:'rgba(255,255,255,0.04)',borderRadius:12,padding:'14px 16px',display:'flex',alignItems:'center',gap:12}}>
      <div style={{color:'rgba(255,255,255,0.3)',flexShrink:0}}>{icon}</div>
      <div>
        <div style={{color:'rgba(255,255,255,0.35)',fontSize:11,marginBottom:2}}>{label}</div>
        <div style={{color:'#fff',fontSize:14,fontWeight:500}}>{value}</div>
      </div>
    </div>
  ) : null;

  const inp = {width:'100%',background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.11)',borderRadius:12,padding:'12px 14px',color:'#fff',fontSize:14,outline:'none',boxSizing:'border-box',transition:'border-color 0.2s'};
  const sel = {...inp, cursor:'pointer', colorScheme:'dark'};

  if (editing) return (
    <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:20,padding:24,marginBottom:24}}>
      <h2 style={{color:'#fff',fontSize:18,fontWeight:700,marginBottom:20}}>Edit Profile</h2>
      <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:12}}>

        {/* First Name */}
        <div>
          <label style={{color:'rgba(255,255,255,0.4)',fontSize:12,display:'block',marginBottom:5}}>First Name</label>
          <input value={form.firstName||''} onChange={e=>setForm({...form,firstName:e.target.value})} style={inp}
            placeholder="First name"
            onFocus={e=>e.target.style.borderColor='#8b5cf6'} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.11)'}/>
        </div>

        {/* Last Name */}
        <div>
          <label style={{color:'rgba(255,255,255,0.4)',fontSize:12,display:'block',marginBottom:5}}>Last Name</label>
          <input value={form.lastName||''} onChange={e=>setForm({...form,lastName:e.target.value})} style={inp}
            placeholder="Last name"
            onFocus={e=>e.target.style.borderColor='#8b5cf6'} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.11)'}/>
        </div>

        {/* Date of Birth */}
        <div>
          <label style={{color:'rgba(255,255,255,0.4)',fontSize:12,display:'block',marginBottom:5}}>Date of Birth</label>
          <input type="date" value={form.dob||''} onChange={e=>setForm({...form,dob:e.target.value})} style={{...inp,colorScheme:'dark'}}/>
        </div>

        {/* Blood Type */}
        <div>
          <label style={{color:'rgba(255,255,255,0.4)',fontSize:12,display:'block',marginBottom:5}}>Blood Type</label>
          <select value={form.bloodType||''} onChange={e=>setForm({...form,bloodType:e.target.value})} style={sel}>
            <option value="" style={{background:'#302b63',color:'#fff'}}>Select blood type...</option>
            {BLOOD_TYPES.map(b=><option key={b} value={b} style={{background:'#302b63',color:'#fff'}}>{b}</option>)}
          </select>
        </div>

        {/* Gender */}
        <div>
          <label style={{color:'rgba(255,255,255,0.4)',fontSize:12,display:'block',marginBottom:5}}>Gender</label>
          <select value={form.gender||''} onChange={e=>setForm({...form,gender:e.target.value})} style={sel}>
            <option value="" style={{background:'#302b63',color:'#fff'}}>Select gender...</option>
            {GENDERS.map(g=><option key={g} value={g} style={{background:'#302b63',color:'#fff'}}>{g}</option>)}
          </select>
        </div>

        {/* Phone with ISD code */}
        <div>
          <label style={{color:'rgba(255,255,255,0.4)',fontSize:12,display:'block',marginBottom:5}}>Phone Number</label>
          <div style={{display:'flex',gap:6}}>
            <select value={form.isdCode||'+91'} onChange={e=>setForm({...form,isdCode:e.target.value})}
              style={{...sel,width:110,flexShrink:0,fontSize:12.5}}>
              {ISD_CODES.map(c=><option key={c.code} value={c.code} style={{background:'#302b63',color:'#fff'}}>{c.flag} {c.code} {c.name}</option>)}
            </select>
            <input value={form.phone||''} onChange={e=>setForm({...form,phone:e.target.value})}
              placeholder="Phone number" style={{...inp,flex:1,marginBottom:0}}
              onFocus={e=>e.target.style.borderColor='#8b5cf6'} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.11)'}/>
          </div>
        </div>

        {/* Address */}
        <div style={{gridColumn:'span 2'}}>
          <label style={{color:'rgba(255,255,255,0.4)',fontSize:12,display:'block',marginBottom:5}}>Home Address</label>
          <input value={form.address||''} onChange={e=>setForm({...form,address:e.target.value})} style={inp}
            placeholder="Street, City, State"
            onFocus={e=>e.target.style.borderColor='#8b5cf6'} onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.11)'}/>
        </div>

      </div>
      <div style={{display:'flex',gap:10,marginTop:20}}>
        <button onClick={save} style={{flex:1,padding:'12px',borderRadius:12,background:'linear-gradient(135deg,#8b5cf6,#f43f5e)',border:'none',color:'#fff',fontWeight:700,fontSize:14,cursor:'pointer',boxShadow:'0 4px 20px rgba(139,92,246,0.35)'}}>
          <Save size={14} style={{marginRight:6,verticalAlign:'middle'}}/>Save Profile
        </button>
        <button onClick={()=>setEditing(false)} style={{padding:'12px 20px',borderRadius:12,background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',color:'rgba(255,255,255,0.55)',fontSize:14,cursor:'pointer'}}>Cancel</button>
      </div>
    </div>
  );

  const hasData = profile.firstName || profile.lastName || profile.dob || profile.bloodType;

  return (
    <div style={{marginBottom:28}}>
      {!hasData ? (
        <div style={{background:'rgba(139,92,246,0.08)',border:'1px dashed rgba(139,92,246,0.35)',borderRadius:20,padding:32,textAlign:'center',marginBottom:24}}>
          <div style={{fontSize:40,marginBottom:12}}>👤</div>
          <div style={{color:'#fff',fontSize:16,fontWeight:600,marginBottom:6}}>Set up your patient profile</div>
          <div style={{color:'rgba(255,255,255,0.4)',fontSize:13,marginBottom:20}}>Add your name, blood type, date of birth and more so doctors have your details instantly.</div>
          <button onClick={()=>setEditing(true)} style={{padding:'11px 28px',borderRadius:50,background:'linear-gradient(135deg,#8b5cf6,#f43f5e)',border:'none',color:'#fff',fontWeight:600,fontSize:13.5,cursor:'pointer',boxShadow:'0 4px 20px rgba(139,92,246,0.4)'}}>
            <Plus size={14} style={{marginRight:6,verticalAlign:'middle'}}/>Set Up Profile
          </button>
        </div>
      ) : (
        <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:20,padding:22,marginBottom:24}}>
          <div style={{display:'flex',justifyContent:'space-between',alignItems:'center',marginBottom:18}}>
            <h2 style={{color:'#fff',fontSize:17,fontWeight:700,margin:0}}>Patient Profile</h2>
            <button onClick={()=>setEditing(true)} style={{display:'flex',alignItems:'center',gap:6,padding:'7px 14px',borderRadius:20,background:'rgba(139,92,246,0.15)',border:'1px solid rgba(139,92,246,0.3)',color:'#a78bfa',fontSize:12.5,fontWeight:600,cursor:'pointer'}}>
              <Edit3 size={12}/>Edit
            </button>
          </div>

          {/* Blood type badge - prominent */}
          {profile.bloodType && (
            <div style={{display:'inline-flex',alignItems:'center',gap:8,background:'linear-gradient(135deg,rgba(244,63,94,0.2),rgba(244,63,94,0.1))',border:'1px solid rgba(244,63,94,0.3)',borderRadius:50,padding:'6px 16px',marginBottom:16}}>
              <Droplet size={14} color="#f43f5e"/>
              <span style={{color:'#f43f5e',fontWeight:700,fontSize:14}}>Blood Type: {profile.bloodType}</span>
            </div>
          )}

          <div style={{display:'grid',gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))',gap:10}}>
            {card('First Name',   profile.firstName,  <User size={15}/>)}
            {card('Last Name',    profile.lastName,   <User size={15}/>)}
            {card('Date of Birth',profile.dob,        <Calendar size={15}/>)}
            {card('Gender',       profile.gender,     <User size={15}/>)}
            {card('Phone', profile.phone ? `${profile.isdCode||'+91'} ${profile.phone}` : '', <Phone size={15}/>)}
            {card('Address',      profile.address,    <FileText size={15}/>)}
          </div>
        </div>
      )}
    </div>
  );
}

// ─── Section Card ─────────────────────────────────────────────────────────────
function SectionCard({ section, items, onUpdate }) {
  const [open,     setOpen]     = useState(false);
  const [adding,   setAdding]   = useState(false);
  const [editIdx,  setEditIdx]  = useState(null);
  const [form,     setForm]     = useState({});
  const Icon = section.icon;
  const count = items?.length || 0;

  const startAdd  = () => { setForm({}); setEditIdx(null); setAdding(true); setOpen(true); };
  const startEdit = (idx) => { setForm({...items[idx]}); setEditIdx(idx); setAdding(true); setOpen(true); };

  const save = () => {
    const filled = Object.values(form).some(v=>String(v||'').trim());
    if (!filled) return;
    if (editIdx !== null) {
      const updated = [...items]; updated[editIdx] = form; onUpdate(updated);
    } else {
      onUpdate([...(items||[]), form]);
    }
    setForm({}); setAdding(false); setEditIdx(null);
  };

  const del = idx => onUpdate((items||[]).filter((_,i)=>i!==idx));

  const inp = {width:'100%',background:'rgba(255,255,255,0.05)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:10,padding:'10px 12px',color:'#fff',fontSize:13,outline:'none',boxSizing:'border-box',marginBottom:8,transition:'border-color 0.2s'};

  return (
    <div style={{background:'rgba(255,255,255,0.04)',border:'1px solid rgba(255,255,255,0.08)',borderRadius:20,overflow:'hidden',transition:'transform 0.18s,box-shadow 0.18s'}}
      onMouseEnter={e=>{e.currentTarget.style.transform='translateY(-3px)';e.currentTarget.style.boxShadow='0 16px 48px rgba(0,0,0,0.35)';}}
      onMouseLeave={e=>{e.currentTarget.style.transform='translateY(0)';e.currentTarget.style.boxShadow='none';}}>

      <button onClick={()=>setOpen(!open)} style={{width:'100%',padding:'16px 18px',background:'transparent',border:'none',cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'space-between',gap:10}}>
        <div style={{display:'flex',alignItems:'center',gap:13}}>
          <div style={{width:40,height:40,borderRadius:13,background:section.bg,display:'flex',alignItems:'center',justifyContent:'center',flexShrink:0}}>
            <Icon size={19} color={section.color}/>
          </div>
          <div style={{textAlign:'left'}}>
            <div style={{color:'#fff',fontWeight:600,fontSize:14}}>{section.label}</div>
            <div style={{color:'rgba(255,255,255,0.32)',fontSize:11.5,marginTop:2}}>
              {count===0 ? 'Tap to add records' : `${count} ${count===1?'record':'records'}`}
            </div>
          </div>
        </div>
        <div style={{display:'flex',alignItems:'center',gap:8}}>
          {count>0 && <span style={{background:section.color,color:'#fff',borderRadius:20,padding:'2px 8px',fontSize:11,fontWeight:700}}>{count}</span>}
          <ChevronDown size={15} color="rgba(255,255,255,0.35)" style={{transform:open?'rotate(180deg)':'rotate(0deg)',transition:'transform 0.2s'}}/>
        </div>
      </button>

      {open && (
        <div style={{padding:'0 14px 14px',borderTop:'1px solid rgba(255,255,255,0.05)'}}>

          {(items||[]).map((item,idx)=>(
            <div key={idx} style={{background:'rgba(255,255,255,0.04)',borderRadius:12,padding:'11px 13px',marginTop:10,display:'flex',justifyContent:'space-between',alignItems:'flex-start',gap:8}}>
              <div style={{flex:1}}>
                {Object.entries(item).filter(([,v])=>v).slice(0,3).map(([k,v])=>(
                  <div key={k} style={{color:'rgba(255,255,255,0.7)',fontSize:12.5,marginBottom:2}}>
                    <span style={{color:'rgba(255,255,255,0.32)',marginRight:4}}>{fieldLabel(k)}:</span>{v}
                  </div>
                ))}
              </div>
              <div style={{display:'flex',gap:6,flexShrink:0}}>
                <button onClick={()=>startEdit(idx)} style={{background:`${section.color}18`,border:`1px solid ${section.color}35`,borderRadius:8,padding:'5px 7px',cursor:'pointer',color:section.color,display:'flex',alignItems:'center'}}>
                  <Edit3 size={12}/>
                </button>
                <button onClick={()=>del(idx)} style={{background:'rgba(251,113,133,0.1)',border:'1px solid rgba(251,113,133,0.2)',borderRadius:8,padding:'5px 7px',cursor:'pointer',color:'#fb7185',display:'flex',alignItems:'center'}}>
                  <Trash2 size={12}/>
                </button>
              </div>
            </div>
          ))}

          {adding ? (
            <div style={{background:'rgba(255,255,255,0.03)',border:`1px solid ${section.color}35`,borderRadius:14,padding:13,marginTop:12}}>
              <div style={{color:'rgba(255,255,255,0.4)',fontSize:12,marginBottom:10}}>
                {editIdx!==null ? 'Edit record' : 'Add new record'}
              </div>
              {section.fields.map(f=>(
                <input key={f} type="text" placeholder={fieldLabel(f)}
                  value={form[f]||''} onChange={e=>setForm({...form,[f]:e.target.value})}
                  style={inp}
                  onFocus={e=>e.target.style.borderColor=section.color}
                  onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.1)'}/>
              ))}
              <div style={{display:'flex',gap:8,marginTop:4}}>
                <button onClick={save} style={{flex:1,padding:'10px',borderRadius:10,background:`linear-gradient(135deg,${section.color},${section.color}bb)`,border:'none',color:'#fff',fontWeight:600,fontSize:13,cursor:'pointer'}}>
                  {editIdx!==null ? 'Update' : 'Save'}
                </button>
                <button onClick={()=>{setAdding(false);setEditIdx(null);setForm({});}} style={{flex:1,padding:'10px',borderRadius:10,background:'rgba(255,255,255,0.06)',border:'1px solid rgba(255,255,255,0.1)',color:'rgba(255,255,255,0.55)',fontSize:13,cursor:'pointer'}}>
                  Cancel
                </button>
              </div>
            </div>
          ) : (
            <button onClick={startAdd}
              style={{width:'100%',marginTop:12,padding:'10px',borderRadius:12,background:'transparent',border:`1px dashed ${section.color}50`,color:section.color,fontSize:13,fontWeight:600,cursor:'pointer',display:'flex',alignItems:'center',justifyContent:'center',gap:6}}
              onMouseEnter={e=>e.currentTarget.style.background=`${section.color}10`}
              onMouseLeave={e=>e.currentTarget.style.background='transparent'}>
              <Plus size={14}/> Add record
            </button>
          )}
        </div>
      )}
    </div>
  );
}

// ─── Login Screen ────────────────────────────────────────────────────────────
function LoginScreen({ onLogin, onSignup }) {
  const [mode,     setMode]     = useState('login');
  const [email,    setEmail]    = useState('');
  const [password, setPassword] = useState('');
  const [error,    setError]    = useState('');
  const [loading,  setLoading]  = useState(false);

  // ── Math captcha ──────────────────────────────────────────────────────────
  const genQ = () => {
    const ops = ['+', '-', '×'];
    const op  = ops[Math.floor(Math.random() * ops.length)];
    let a, b, ans;
    if (op === '+') { a = Math.floor(Math.random()*20)+1; b = Math.floor(Math.random()*20)+1; ans = a+b; }
    if (op === '-') { a = Math.floor(Math.random()*20)+10; b = Math.floor(Math.random()*10)+1; ans = a-b; }
    if (op === '×') { a = Math.floor(Math.random()*9)+2;  b = Math.floor(Math.random()*9)+2;  ans = a*b; }
    return { q:`${a} ${op} ${b} = ?`, ans };
  };
  const [captcha,    setCaptcha]    = useState(() => genQ());
  const [captchaVal, setCaptchaVal] = useState('');
  const [captchaOk,  setCaptchaOk]  = useState(false);
  const [captchaErr, setCaptchaErr] = useState(false);

  const checkCaptcha = (val) => {
    setCaptchaVal(val);
    setCaptchaErr(false);
    if (val.trim() === String(captcha.ans)) { setCaptchaOk(true); }
    else { setCaptchaOk(false); }
  };

  const refreshCaptcha = () => {
    setCaptcha(genQ());
    setCaptchaVal('');
    setCaptchaOk(false);
    setCaptchaErr(false);
  };

  const switchMode = (m) => {
    setMode(m); setError('');
    refreshCaptcha();
  };

  const submit = async () => {
    if (!captchaOk) { setCaptchaErr(true); setError('Please answer the security question correctly.'); return; }
    setLoading(true); setError('');
    try {
      if (mode === 'login') await onLogin(email, password);
      else                  await onSignup(email, password);
    } catch(e) {
      // Show exact error so we can debug
      let msg = e.message || 'Unknown error. Please try again.';
      if (msg.includes('Invalid login') || msg.includes('invalid_grant') || msg.includes('Invalid email or password')) {
        msg = 'Incorrect email or password. Please try again.';
      } else if (msg.includes('Email not confirmed') || msg.includes('confirm')) {
        msg = 'Email not confirmed. Please check your inbox for a confirmation email.';
      } else if (msg.includes('already registered') || msg.includes('already been registered')) {
        msg = 'This email is already registered. Please sign in instead.';
      } else if (msg.includes('Password should be')) {
        msg = 'Password must be at least 6 characters.';
      } else if (msg.includes('network') || msg.includes('fetch') || msg.includes('Failed to fetch')) {
        msg = 'Connection error. Please check your internet and try again.';
      }
      // Show full error for debugging
      setError(msg + ' [Debug: ' + e.message + ']');
      refreshCaptcha();
    } finally { setLoading(false); }
  };

  const inp = {
    width:'100%', background:'rgba(255,255,255,0.06)',
    border:'1px solid rgba(255,255,255,0.11)', borderRadius:13,
    padding:'13px 15px', color:'#fff', fontSize:14.5,
    outline:'none', boxSizing:'border-box', transition:'border-color 0.2s',
  };

  return (
    <div style={{minHeight:'100vh',background:'linear-gradient(135deg,#0f0c29 0%,#302b63 55%,#24243e 100%)',display:'flex',alignItems:'center',justifyContent:'center',padding:20,fontFamily:"'Inter',system-ui,sans-serif"}}>

      {/* Glow blobs */}
      <div style={{position:'fixed',top:'-15%',left:'-5%',width:480,height:480,borderRadius:'50%',background:'radial-gradient(circle,rgba(139,92,246,0.18) 0%,transparent 70%)',pointerEvents:'none'}}/>
      <div style={{position:'fixed',bottom:'-15%',right:'-5%',width:480,height:480,borderRadius:'50%',background:'radial-gradient(circle,rgba(244,63,94,0.14) 0%,transparent 70%)',pointerEvents:'none'}}/>

      <div style={{width:'100%',maxWidth:420}}>

        {/* Logo */}
        <div style={{textAlign:'center',marginBottom:36}}>
          <div style={{width:70,height:70,borderRadius:22,background:'linear-gradient(135deg,#f43f5e,#8b5cf6)',display:'flex',alignItems:'center',justifyContent:'center',fontSize:30,margin:'0 auto 18px',boxShadow:'0 16px 48px rgba(139,92,246,0.45)'}}>🏥</div>
          <h1 style={{color:'#fff',fontSize:30,fontWeight:800,letterSpacing:'-0.5px',margin:'0 0 8px'}}>MediCard</h1>
          <p style={{color:'rgba(255,255,255,0.38)',fontSize:14.5,margin:0}}>Your health story, always with you</p>
        </div>

        {/* Card */}
        <div style={{background:'rgba(255,255,255,0.05)',backdropFilter:'blur(24px)',border:'1px solid rgba(255,255,255,0.1)',borderRadius:24,padding:28}}>

          {/* Mode toggle */}
          <div style={{display:'flex',background:'rgba(255,255,255,0.06)',borderRadius:12,padding:4,marginBottom:24}}>
            {['login','signup'].map(m=>(
              <button key={m} onClick={()=>switchMode(m)}
                style={{flex:1,padding:'9px',borderRadius:9,background:mode===m?'rgba(255,255,255,0.11)':'transparent',border:'none',color:mode===m?'#fff':'rgba(255,255,255,0.38)',fontSize:13.5,fontWeight:mode===m?600:400,cursor:'pointer',transition:'all 0.2s'}}>
                {m==='login'?'Sign in':'Create account'}
              </button>
            ))}
          </div>

          {/* Error */}
          {error && (
            <div style={{background:'rgba(244,63,94,0.1)',border:'1px solid rgba(244,63,94,0.25)',borderRadius:11,padding:'11px 14px',marginBottom:16,color:'#fca5a5',fontSize:13}}>
              {error}
            </div>
          )}

          {/* Fields */}
          <div style={{display:'flex',flexDirection:'column',gap:11}}>
            <input type="email" placeholder="Email address" value={email}
              onChange={e=>setEmail(e.target.value)} style={inp}
              onFocus={e=>e.target.style.borderColor='#8b5cf6'}
              onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.11)'}/>
            <input type="password" placeholder="Password (min 6 characters)" value={password}
              onChange={e=>setPassword(e.target.value)} style={inp}
              onFocus={e=>e.target.style.borderColor='#8b5cf6'}
              onBlur={e=>e.target.style.borderColor='rgba(255,255,255,0.11)'}
              onKeyDown={e=>e.key==='Enter'&&submit()}/>
          </div>

          {/* Math Captcha */}
          <div style={{marginTop:18,padding:'16px',background:'rgba(255,255,255,0.04)',borderRadius:14,border:captchaErr?'1px solid rgba(244,63,94,0.4)':'1px solid rgba(255,255,255,0.08)'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:12}}>
              <span style={{color:'rgba(255,255,255,0.5)',fontSize:12}}>🔒 Security check</span>
              <button onClick={refreshCaptcha} style={{background:'transparent',border:'none',color:'rgba(255,255,255,0.35)',fontSize:11,cursor:'pointer',padding:'2px 6px'}}>↻ New question</button>
            </div>
            <div style={{display:'flex',alignItems:'center',gap:12}}>
              <div style={{flex:1,background:'linear-gradient(135deg,rgba(139,92,246,0.15),rgba(244,63,94,0.15))',borderRadius:10,padding:'12px 16px',textAlign:'center'}}>
                <span style={{color:'#fff',fontSize:20,fontWeight:700,letterSpacing:2}}>{captcha.q}</span>
              </div>
              <input
                type="number"
                placeholder="?"
                value={captchaVal}
                onChange={e=>checkCaptcha(e.target.value)}
                onKeyDown={e=>e.key==='Enter'&&submit()}
                style={{width:72,background:captchaOk?'rgba(52,211,153,0.1)':'rgba(255,255,255,0.06)',border:captchaOk?'1px solid rgba(52,211,153,0.4)':captchaErr?'1px solid rgba(244,63,94,0.4)':'1px solid rgba(255,255,255,0.15)',borderRadius:10,padding:'12px 8px',color:'#fff',fontSize:18,fontWeight:700,textAlign:'center',outline:'none'}}
              />
              {captchaOk && <span style={{fontSize:22}}>✅</span>}
            </div>
          </div>

          {/* Submit */}
          <button onClick={submit} disabled={loading}
            style={{width:'100%',marginTop:16,padding:'14px',borderRadius:13,
              background: loading?'rgba(139,92,246,0.35)':'linear-gradient(135deg,#8b5cf6,#f43f5e)',
              border:'none',color:'#fff',fontSize:14.5,fontWeight:700,
              cursor: loading?'not-allowed':'pointer',
              boxShadow: loading?'none':'0 8px 28px rgba(139,92,246,0.45)',
              transition:'all 0.2s',letterSpacing:'0.1px'}}>
            {loading?'Please wait…':mode==='login'?'Sign in →':'Create account →'}
          </button>

          {/* Footer */}
          <div style={{display:'flex',justifyContent:'center',gap:20,marginTop:20}}>
            {['🔒 Encrypted','📱 Offline ready','☁️ Auto-sync'].map(t=>(
              <span key={t} style={{color:'rgba(255,255,255,0.25)',fontSize:11}}>{t}</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
